import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from './Button';
import { cn } from '../../utils/cn';

export const ThemeToggle = ({ className }) => {
  const { theme, resolvedTheme, setTheme, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  const currentOption = themeOptions.find(opt => opt.value === theme) || themeOptions[2];
  const CurrentIcon = currentOption.icon;

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
        aria-label="Change theme"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <CurrentIcon className="h-4 w-4" />
        <span className="hidden sm:inline">{currentOption.label}</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', isOpen && 'rotate-180')} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-40 rounded-lg border border-border bg-surface shadow-lg">
          <div className="p-1">
            {themeOptions.map(option => {
              const OptionIcon = option.icon;
              const isSelected = theme === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    setTheme(option.value);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'transition-theme flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                    'hover:bg-surface-elevated',
                    'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    isSelected && 'bg-primary/10 text-primary'
                  )}
                >
                  <OptionIcon className="h-4 w-4" />
                  <span className="flex-1 text-left">{option.label}</span>
                  {isSelected && <span className="text-primary">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
